import { ArrowLeft } from "lucide-react"

import Logo from "@/assets/img/soulmate/logo.png"
import RatingStars from "@/assets/img/soulmate/rating-stars.png"
import StarGold from "@/assets/img/soulmate/star-gold.svg"
import TopGlow from "@/assets/img/soulmate/top-glow.png"
import WreathLeft from "@/assets/img/soulmate/wreath-left.png"
import WreathRight from "@/assets/img/soulmate/wreath-right.png"
import { Button } from "@/components/ui/button"
import type { StepComponentProps } from "./step-contract"

/** Social-proof reviews (Figma Step0.1). */
const REVIEWS = [
  {
    text: "Seeing my soulmate sketch honestly gave me goosebumps. It felt strangely personal and accurate.",
    name: "Meera Rajan",
    rating: "4.8",
  },
  {
    text: "I didn't expect it to feel this real. Some details instantly reminded me of someone I know.",
    name: "Sungirl",
    rating: "4.8",
  },
]

function ReviewStars() {
  return (
    <span className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <img key={i} src={StarGold} alt="" className="size-4" />
      ))}
    </span>
  )
}

/**
 * Soulmate social-proof interstitial (Figma Step0.1): logo, trust headline and a
 * dark-surface reviews card — a wreath rating header plus a horizontally
 * scrollable review slider — then Continue. Sits before the counted quiz, so it
 * renders its own back-only navbar with no step counter.
 */
export function SoulmateSocialProofStep({ step, onAdvance, onBack }: StepComponentProps) {
  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-dark-base text-purple-light">
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-6 px-4">
          <div className="flex flex-col items-center gap-4">
            <img src={Logo} alt="" className="size-22 rounded-[25px]" />
            <h1 className="text-center font-display text-2xl font-medium text-purple-light">
              {step.title}
            </h1>
          </div>

          <div className="flex flex-col gap-4 rounded-[24px] bg-dark-surface px-4 pt-4 pb-6">
            <div className="flex items-center justify-center gap-2">
              <img src={WreathLeft} alt="" className="h-20 w-[34px]" />
              <div className="flex flex-col items-center gap-1">
                <span className="font-display text-xl font-medium text-purple-light">
                  Our users trust us
                </span>
                <img src={RatingStars} alt="" className="h-6 w-auto" />
                <span className="text-xs text-purple-light/40">more than 10k reviews</span>
              </div>
              <img src={WreathRight} alt="" className="h-20 w-[34px]" />
            </div>

            <div className="no-scrollbar flex gap-4 overflow-x-auto">
              {REVIEWS.map((review) => (
                <div
                  key={review.name}
                  className="flex w-[300px] shrink-0 flex-col gap-6 rounded-2xl border border-purple-light/20 p-4"
                >
                  <p className="text-base text-purple-light/70">{review.text}</p>
                  <div className="flex flex-col gap-2">
                    <span className="font-display text-base font-medium text-purple-light">
                      {review.name}
                    </span>
                    <span className="flex items-center gap-2">
                      <ReviewStars />
                      <span className="text-sm font-medium text-gold">{review.rating}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 pt-2 pb-safe">
          <Button variant="gradient" size="xl" onClick={onAdvance}>
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}
